import { useActionsTrack } from "@/_old/features/analytics";
import { useFilters } from "@/_old/features/filters";
import { Group } from "@/company/groups/types";
import { Member } from "@/company/members/types";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/core/components/ui/sheet";
import { formatToISODate } from "@/core/utils";
import { Button } from "@woba/ds-web-next";
import { Eraser } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDashboardFilters } from "../store/useDashboardFilters";
import { DashboardFilters } from "../types";
import { wobaText } from "./dashboard-ds";
import { DateQuickFilters } from "./dashboard-filters/components/date-filters/components/date-quick-filters";
import { DASHBOARD_SHEET_FILTER_LOCATION } from "./dashboard-filters/components/ds-filter-field";
import { GroupFilter } from "./dashboard-filters/components/group-filter";
import { LocationFilters } from "./dashboard-filters/components/location-filters";
import { MemberFilter } from "./dashboard-filters/components/member-filter";
import { SpaceTypeFilter } from "./dashboard-filters/components/space-type-filter";
import { StatusFilter } from "./dashboard-filters/components/status-filter";

interface DashboardFiltersSheetProps extends React.ComponentPropsWithoutRef<
  typeof Sheet
> {}

export const DashboardFiltersSheet = ({
  ...props
}: DashboardFiltersSheetProps) => {
  const { onActionView } = useActionsTrack();

  useEffect(() => {
    if (props.open) {
      onActionView("DASHBOARD_FILTERS_SHEET_VIEW");
    }
  }, [props.open]);

  const [translate] = useTranslation(["dashboard"]);
  const { dateLimits, groupId } = useFilters();
  const { filters, setFilters, setFilterCount } = useDashboardFilters();
  const [filterCollection, setFilterCollection] =
    useState<DashboardFilters | null>(null);

  const handleChange = useCallback(
    (value: DashboardFilters) => {
      setFilterCollection(value);
    },
    [setFilterCollection],
  );

  const handleSubmit = useCallback(() => {
    if (filterCollection) {
      setFilters(filterCollection as DashboardFilters);
      setFilterCount(
        Object.keys(filterCollection as DashboardFilters).filter(
          (key) =>
            filterCollection[key as keyof DashboardFilters] !== undefined &&
            key !== "datePreset",
        ).length,
      );
      props.onOpenChange?.(false);
    }
  }, [filterCollection, setFilters, props.onOpenChange, setFilterCount]);

  const handleReset = useCallback(() => {
    const start = formatToISODate(dateLimits?.startDate as Date);
    const end = formatToISODate(dateLimits?.endDate as Date);

    setFilters({
      startDate: start,
      endDate: end,
      datePreset: "today",
      group_id: (groupId as number) ?? null,
      type: [],
      status: [],
      users: [],
      cities: [],
      spaces: [],
      neighborhoods: [],
      companyId: null,
    } as DashboardFilters);
    setFilterCount(0);
  }, [setFilters, setFilterCount]);

  // Esc com um InputSelect do DS aberto fecha só o menu, não a modal: o
  // Radix escuta o Esc em captura, antes do InputSelect tratar a tecla.
  const handleEscapeKeyDown = useCallback((event: KeyboardEvent) => {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('[role="combobox"][aria-expanded="true"]')
    ) {
      event.preventDefault();
    }
  }, []);

  return (
    <Sheet {...props}>
      <SheetContent
        className="overflow-y-auto !px-0 !pb-0"
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <div className="px-4 lg:px-8">
          <SheetHeader>
            <SheetTitle style={wobaText.headingSmall}>
              {translate("dashboard-filters-title")}
            </SheetTitle>
          </SheetHeader>

          {/* Mesmos campos do Workbase DS da barra de filtros do Painel:
              Chips (Data), InputSelect (Tipos de espaço, Status) e o visual de
              InputSelect nos campos com busca/paginação. */}
          <div className="flex flex-col gap-6 py-4 dashboard-filters-sheet--container">
            <DateQuickFilters
              appearance="chips"
              onSubmit={true}
              onChange={(
                value: {
                  startDate: string;
                  endDate: string;
                  datePreset: string;
                } | null,
              ) =>
                handleChange({
                  ...filterCollection,
                  startDate: value?.startDate as string,
                  endDate: value?.endDate as string,
                  datePreset: value?.datePreset,
                } as DashboardFilters)
              }
            />
            <MemberFilter
              location={DASHBOARD_SHEET_FILTER_LOCATION}
              onSubmit={true}
              onChange={(value: Member[] | null) =>
                handleChange({
                  ...filterCollection,
                  users: value as Member[],
                } as DashboardFilters)
              }
            />
            <SpaceTypeFilter
              location={DASHBOARD_SHEET_FILTER_LOCATION}
              onSubmit={true}
              onChange={(value: string[] | null) =>
                handleChange({
                  ...filterCollection,
                  type: value as string[],
                } as DashboardFilters)
              }
            />
            <LocationFilters
              location={DASHBOARD_SHEET_FILTER_LOCATION}
              onSubmit={true}
              onChange={(value, type) =>
                handleChange({
                  ...filterCollection,
                  [type]: value as { id: string; name: string }[],
                } as DashboardFilters)
              }
            />
            <StatusFilter
              location={DASHBOARD_SHEET_FILTER_LOCATION}
              onSubmit={true}
              onChange={(value: number[] | null) =>
                handleChange({
                  ...filterCollection,
                  status: value as number[],
                } as DashboardFilters)
              }
            />
            <GroupFilter
              location={DASHBOARD_SHEET_FILTER_LOCATION}
              onSubmit={true}
              asCheckbox={true}
              onChange={(value: Group | null) =>
                handleChange({
                  ...filterCollection,
                  group_id: value?.id as number,
                } as DashboardFilters)
              }
            />
          </div>
        </div>

        <SheetFooter className="sticky bottom-0 mt-auto border-t border-[color:var(--woba-color-stroke-default)] bg-[color:var(--woba-color-surface-default)]">
          <div className="flex gap-4 justify-between items-center px-4 lg:px-8">
            <Button
              appearance="critical-ghost"
              size="large"
              leading
              leadingIcon={({ color, size }) => (
                <Eraser color={color} size={size} />
              )}
              accessibilityLabel={
                translate("dashboard-filters-reset") as string
              }
              onPress={handleReset}
            >
              {translate("dashboard-filters-reset") as string}
            </Button>

            <Button
              appearance="primary"
              size="large"
              accessibilityLabel={
                translate("dashboard-filters-apply") as string
              }
              onPress={handleSubmit}
            >
              {translate("dashboard-filters-apply") as string}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
